import { useId, useState } from "react"

import { StringValueYesNoChip } from "@/metrics/metric-display"
import { MetricRecord } from "@/metrics/runtime"
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { Plus, X } from "lucide-react"

import { logger } from "@/lib/logger"
import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type { JsonObject } from "@/types/json"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"

import { AggregateOverviewString } from "../components/aggregate/AggregateOverviewString"
import { SingleBinary } from "../components/single/SingleBinary"
import { RequiredMarker } from "../configuration/components/RequiredMarker"
import {
  ApiStateDeepDive,
  getApiStateExplanation,
  MATCH_RESULT_LABELS,
  MATCH_RESULTS,
  toBinaryMo,
  type MatchResult,
} from "./components/ApiStateDeepDive"
import { ExpectedBodyInput } from "./components/ExpectedBodyInput"
import { MatchPathInput } from "./components/MatchPathInput"

const log = logger("ApiStateMetric")

interface HeadersEditorProps {
  headers: Record<string, string>
  onAddHeader: (key: string, value: string, clearInputs: () => void) => void
  onRemoveHeader: (key: string) => void
}

const HeadersEditor: React.FC<HeadersEditorProps> = ({
  headers,
  onAddHeader,
  onRemoveHeader,
}) => {
  const headingId = useId()
  const [headerKey, setHeaderKey] = useState("")
  const [headerValue, setHeaderValue] = useState("")

  const clearInputs = () => {
    setHeaderKey("")
    setHeaderValue("")
  }

  const handleAdd = () => {
    onAddHeader(headerKey, headerValue, clearInputs)
  }

  return (
    <div
      role="group"
      aria-labelledby={headingId}
      className="flex flex-col gap-2"
    >
      <span id={headingId} className="text-sm font-medium">
        Headers (Optional)
      </span>

      {Object.entries(headers).length > 0 && (
        <div className="space-y-2 mb-2">
          {Object.entries(headers).map(([key, value]) => (
            <div
              key={key}
              className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg"
            >
              <div className="flex-1 grid grid-cols-2 gap-2">
                <span className="font-mono text-sm">{key}</span>
                <span className="font-mono text-sm text-muted-foreground">
                  {String(value)}
                </span>
              </div>
              <Button
                variant="danger"
                icon
                onClick={() => onRemoveHeader(key)}
                tooltip="Remove header"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Input
          placeholder="Header name"
          value={headerKey}
          onChange={(e) => setHeaderKey(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              handleAdd()
            }
          }}
          aria-label="Header name"
        />
        <Input
          placeholder="Header value"
          value={headerValue}
          onChange={(e) => setHeaderValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              handleAdd()
            }
          }}
          aria-label="Header value"
        />
        <Button
          variant="secondary"
          icon
          onClick={handleAdd}
          disabled={!headerKey.trim()}
          tooltip="Add header"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}

interface ApiStateMetadataEditorProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

const ApiStateMetadataEditor: React.FC<ApiStateMetadataEditorProps> = ({
  metricRecordData,
  onChange,
}) => {
  const fieldId = useId()
  const headers = metricRecordData.metric_metadata?.headers || {}

  const handleAddHeader = (
    headerKey: string,
    headerValue: string,
    clearInputs: () => void,
  ) => {
    if (headerKey.trim()) {
      onChange({
        ...metricRecordData,
        metric_metadata: {
          ...metricRecordData.metric_metadata,
          headers: {
            ...headers,
            [headerKey.trim()]: headerValue.trim(),
          },
        },
      })
      clearInputs()
    }
  }

  const handleRemoveHeader = (key: string) => {
    const newHeaders = { ...headers }
    delete newHeaders[key]
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        headers: newHeaders,
      },
    })
  }

  const method = metricRecordData.metric_metadata?.method || "GET"

  // Held as an object once committed on blur, and as raw text while editing.
  const requestBody = metricRecordData.metric_metadata?.request_body
  const requestBodyText =
    typeof requestBody === "object" && requestBody !== null
      ? JSON.stringify(requestBody, null, 2)
      : typeof requestBody === "string"
        ? requestBody
        : "{\n  \n}"

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-2">
        <label htmlFor={`${fieldId}-url`} className="text-sm font-medium">
          API URL
          <RequiredMarker />
        </label>
        <Input
          id={`${fieldId}-url`}
          placeholder="https://api.example.com/endpoint"
          value={metricRecordData.metric_metadata?.api_url || ""}
          required
          onChange={(e) => {
            onChange({
              ...metricRecordData,
              metric_metadata: {
                ...metricRecordData.metric_metadata,
                api_url: e.target.value,
              },
            })
          }}
        />
        <p className="text-xs text-coval-text-muted">
          The URL of the API endpoint to call
        </p>
      </div>

      <div className="space-y-2">
        <label
          htmlFor={`${fieldId}-method`}
          id={`${fieldId}-method-label`}
          className="text-sm font-medium"
        >
          HTTP Method
        </label>
        <Select
          value={method}
          onValueChange={(value) => {
            onChange({
              ...metricRecordData,
              metric_metadata: {
                ...metricRecordData.metric_metadata,
                method: value,
              },
            })
          }}
        >
          <SelectTrigger
            id={`${fieldId}-method`}
            aria-labelledby={`${fieldId}-method-label`}
          >
            <SelectValue placeholder="Select HTTP method" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="GET">GET</SelectItem>
            <SelectItem value="POST">POST</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-coval-text-muted">
          HTTP method to use for the request
        </p>
      </div>

      {method === "POST" && (
        <div className="space-y-2">
          <span id={`${fieldId}-body`} className="text-sm font-medium">
            Request Body (JSON)
          </span>
          <JsonCodeEditor
            placeholder={
              '{\n  "key": "value"\n}\n\nor use template variables like {{agent.api_key}}'
            }
            value={requestBodyText}
            rows={3}
            onChange={(value) => {
              onChange({
                ...metricRecordData,
                metric_metadata: {
                  ...metricRecordData.metric_metadata,
                  request_body: value,
                },
              })
            }}
            onBlur={() => {
              const value = requestBodyText.trim()
              if (!value) {
                onChange({
                  ...metricRecordData,
                  metric_metadata: {
                    ...metricRecordData.metric_metadata,
                    request_body: {},
                  },
                })
                return
              }
              try {
                const parsed: unknown = JSON.parse(value)
                if (
                  typeof parsed === "object" &&
                  parsed !== null &&
                  !Array.isArray(parsed)
                ) {
                  onChange({
                    ...metricRecordData,
                    metric_metadata: {
                      ...metricRecordData.metric_metadata,
                      request_body: parsed as JsonObject,
                    },
                  })
                } else {
                  log.error("Request body must be a JSON object")
                }
              } catch (error) {
                log.error(
                  "Invalid JSON in request body",
                  error instanceof Error ? error : undefined,
                )
              }
            }}
            // Template variables may stand in for whole values, which a plain
            // parse rejects; the blur handler logs anything it cannot store.
            lint={false}
            ariaLabelledBy={`${fieldId}-body`}
          />
          <p className="text-xs text-coval-text-muted">
            Request body to send as JSON object. Will be validated on save.
            Supports template variables.
          </p>
        </div>
      )}

      <ExpectedBodyInput
        value={metricRecordData.metric_metadata?.expected_body || ""}
        onChange={(value) => {
          onChange({
            ...metricRecordData,
            metric_metadata: {
              ...metricRecordData.metric_metadata,
              expected_body: value,
            },
          })
        }}
        isRequired
      />

      <MatchPathInput
        value={metricRecordData.metric_metadata?.match_path || ""}
        onChange={(value) => {
          onChange({
            ...metricRecordData,
            metric_metadata: {
              ...metricRecordData.metric_metadata,
              match_path: value,
            },
          })
        }}
        expectedBodyValue={metricRecordData.metric_metadata?.expected_body}
      />

      <div className="space-y-2">
        <label htmlFor={`${fieldId}-timeout`} className="text-sm font-medium">
          Timeout (seconds)
        </label>
        <Input
          id={`${fieldId}-timeout`}
          placeholder="30"
          type="number"
          value={metricRecordData.metric_metadata?.timeout?.toString() || "30"}
          onChange={(e) => {
            onChange({
              ...metricRecordData,
              metric_metadata: {
                ...metricRecordData.metric_metadata,
                timeout: parseInt(e.target.value, 10) || 30,
              },
            })
          }}
        />
        <p className="text-xs text-coval-text-muted">
          Request timeout in seconds
        </p>
      </div>

      <HeadersEditor
        headers={headers}
        onAddHeader={(key, value, clearInputs) =>
          handleAddHeader(key, value, clearInputs)
        }
        onRemoveHeader={handleRemoveHeader}
      />
    </div>
  )
}

export class ApiStateMetricRecord extends MetricRecord {
  overviewSize = "medium" as const
  aggregate_units = ""

  /**
   * Preprocess API State metric before saving to ensure request_body is always an object
   */
  preprocessMetadataBeforeSave(): ApiStateMetricRecord {
    // Only preprocess if request_body exists and method is POST
    if (
      this.metric_metadata?.method === "POST" &&
      this.metric_metadata?.request_body
    ) {
      const requestBody = this.metric_metadata.request_body
      if (typeof requestBody === "string") {
        try {
          const trimmed = requestBody.trim()
          const parsed: unknown = JSON.parse(trimmed)
          if (typeof parsed === "object" && parsed !== null) {
            // Update this instance's metadata
            this.metric_metadata = {
              ...this.metric_metadata,
              request_body: parsed as JsonObject,
            }
          } else {
            throw new Error("Request body must be a JSON object")
          }
        } catch (error) {
          throw new Error(
            `Invalid JSON in request body: ${error instanceof Error ? error.message : "Unknown error"}`,
          )
        }
      }
    }
    return this
  }

  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    return (
      <StringValueYesNoChip mo={toBinaryMo(mo)} labels={MATCH_RESULT_LABELS} />
    )
  }

  getSingleMetricOutputNode(mo: RunMetricOutput): React.ReactNode | null {
    return <SingleBinary mo={toBinaryMo(mo)} labels={MATCH_RESULT_LABELS} />
  }

  getDisplayValueString(mo: MetricOutputDisplaySource): string | null {
    const matchResult = mo.string_value as MatchResult | null
    if (matchResult === MATCH_RESULTS.MATCH) {
      return MATCH_RESULT_LABELS.yes
    }
    if (matchResult === MATCH_RESULTS.DIFF) {
      return MATCH_RESULT_LABELS.no
    }
    if (matchResult === MATCH_RESULTS.NOT_FOUND) {
      return MATCH_RESULT_LABELS.unknown
    }
    return mo.string_value ?? null
  }

  getOverviewNode(
    mo: RunMetricOutput[],
    controlledProps?: {
      includeUnknowns?: boolean
      onIncludeUnknownsChange?: (value: boolean) => void
      hideCheckbox?: boolean
    },
  ): React.ReactNode {
    const normalized = mo.map((m) => toBinaryMo(m))
    return (
      <AggregateOverviewString
        mos={normalized}
        isBinary
        binaryLabels={MATCH_RESULT_LABELS}
        includeUnknowns={controlledProps?.includeUnknowns}
        onIncludeUnknownsChange={controlledProps?.onIncludeUnknownsChange}
        hideCheckbox={controlledProps?.hideCheckbox}
      />
    )
  }

  renderInDepthRunReview(
    metricOutputs: RunMetricOutput[],
    context?: {
      runRows?: RunRowLike[]
      highlightValue?: number | string | null
      onBarClick?: (filter: { type: "exact"; value: string }) => void
      activeFilters?: ChartBarFilter[]
    },
  ): React.ReactNode {
    if (metricOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">
          No metric data available for this metric.
        </div>
      )
    }
    const normalized = metricOutputs.map((m) => toBinaryMo(m))
    return (
      <div className="w-full">
        <AggregateOverviewString
          mos={normalized}
          isBinary
          binaryLabels={MATCH_RESULT_LABELS}
          onBarClick={context?.onBarClick}
          activeFilters={context?.activeFilters}
        />
      </div>
    )
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    try {
      const explanation = getApiStateExplanation(
        mo,
        this.metric_metadata as Record<string, unknown> | undefined,
      )
      if (!explanation) {
        return (
          <div className="text-coval-text-muted">
            No detailed results available
          </div>
        )
      }
      const matchResult: MatchResult =
        explanation.match_result ||
        (mo.string_value as MatchResult) ||
        (mo.float_value === 1 ? MATCH_RESULTS.MATCH : MATCH_RESULTS.DIFF)

      return (
        <ApiStateDeepDive explanation={explanation} matchResult={matchResult} />
      )
    } catch (error) {
      log.error("Error parsing API state metric explanation", error)
      return <div className="text-red-500">Error displaying metric details</div>
    }
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
  ): React.ReactNode {
    return (
      <ApiStateMetadataEditor
        metricRecordData={metricRecordData}
        onChange={onChange}
      />
    )
  }

  renderMetadataField(metricRecordData: MetricRecordData): React.ReactNode {
    const headers = metricRecordData.metric_metadata?.headers || {}
    const method = metricRecordData.metric_metadata?.method || "GET"
    return (
      <div className="flex flex-col gap-3">
        <div>
          <h3 className="font-semibold">API URL</h3>
          <p className="font-mono text-sm break-all">
            {metricRecordData.metric_metadata?.api_url || "Not set"}
          </p>
        </div>
        <div>
          <h3 className="font-semibold">HTTP Method</h3>
          <p>{method}</p>
        </div>
        {method === "POST" &&
          metricRecordData.metric_metadata?.request_body &&
          ((typeof metricRecordData.metric_metadata.request_body === "object" &&
            Object.keys(metricRecordData.metric_metadata.request_body).length >
              0) ||
            (typeof metricRecordData.metric_metadata.request_body ===
              "string" &&
              metricRecordData.metric_metadata.request_body.length > 0)) && (
            <div>
              <h3 className="font-semibold">Request Body</h3>
              <pre className="font-mono text-sm bg-muted/30 p-2 rounded">
                {typeof metricRecordData.metric_metadata.request_body ===
                "object"
                  ? JSON.stringify(
                      metricRecordData.metric_metadata.request_body,
                      null,
                      2,
                    )
                  : metricRecordData.metric_metadata.request_body}
              </pre>
            </div>
          )}
        <div>
          <h3 className="font-semibold">Expected Body</h3>
          <pre className="font-mono text-sm bg-muted/30 p-2 rounded">
            {metricRecordData.metric_metadata?.expected_body || "Not set"}
          </pre>
        </div>
        {metricRecordData.metric_metadata?.match_path && (
          <div>
            <h3 className="font-semibold">Match Path</h3>
            <p className="font-mono text-sm">
              {metricRecordData.metric_metadata.match_path}
            </p>
          </div>
        )}
        <div>
          <h3 className="font-semibold">Timeout</h3>
          <p>{metricRecordData.metric_metadata?.timeout || 30} seconds</p>
        </div>
        {Object.keys(headers).length > 0 && (
          <div>
            <h3 className="font-semibold">Headers</h3>
            <div className="space-y-1">
              {Object.entries(headers).map(([key, value]) => (
                <div key={key} className="font-mono text-sm">
                  <span className="text-muted-foreground">{key}:</span>{" "}
                  {String(value)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  renderMetricType(): React.ReactNode {
    return "API State"
  }
}
